/**
 * Extract the base part of a given URL, starting from the protocol to hostname, and custom port if necessary.
 * @param url - the URL to extract the base from
 * @returns base of the URL, eg, http://localhost
 */
export const baseUrlOf = (url: string) => {
  const parsed = new URL(url);
  const port = parsed.port?.length ? ':' + parsed.port : '';
  return parsed.protocol + '//' + parsed.hostname + port;
};

/**
 * Strip the protocol and first subdomain from a given URL.
 * @param url - the URL to strip
 * @returns the URL without the protocol and subdomain
 */
export const stripUrl = (url: string) => {
  const hostname = new URL(url).hostname;

  // Split the hostname
  const urlParts = hostname.split('.');

  /**
   * TLDs can be multi-part (eg .com.au) or single (eg .com, .au).
   *
   * Strip the first subdomain while preserving domains like leafio.com.au, or
   * leafio.au.
   */
  const topLevel = urlParts[urlParts.length - 1];
  const secondLevel = urlParts[urlParts.length - 2];
  const isCountryCode = topLevel.length === 2;
  const genericTopLevelDomains = ['com', 'co', 'org', 'net', 'gov', 'edu'];

  // multi level TLDs like .com.au
  const isMultiTopLevel = isCountryCode && genericTopLevelDomains.includes(secondLevel);

  const labelsToKeep = isMultiTopLevel ? 3 : 2;

  return urlParts.slice(-labelsToKeep).join('.');
};

/**
 * Extract the relative path from a given URL, including the pathname and query string.
 * @param url - the full URL to extract the path from
 * @returns the relative path with query params
 */
export const relativePathOf = (url: string) => {
  const parsed = new URL(url);
  return parsed.pathname + parsed.search;
};
